import {AnimatePresence, motion} from 'framer-motion';
import {X} from 'lucide-react';
import * as React from 'react';
import {createPortal} from 'react-dom';
import {useBottomBarSlot} from 'src/BottomBar';
import {Button} from 'src/Button';
import Layer from 'src/Layer/Layer';
import {cn} from 'src/lib/utils';
import {PopoverMenu} from 'src/PopoverMenu';
import {useIsMobile} from 'src/useIsMobile';

type Props = {
  /** Shown while true: rows are selected, or select mode is on. */
  open: boolean;
  count: number;
  /** The selection's total, one line however many currencies. */
  total: string;
  /** The full set of actions, under More. */
  menuItems: React.ReactNode;
  /** The one action the list promotes beside More. */
  primary?: {label: string; disabled?: boolean; onClick: () => void};
  onCancel: () => void;
};

/**
 * The bar every list shows while rows are selected: the count, the running
 * total, More, and the list's primary action. On a phone it takes the tab
 * bar's own box (see BottomBarSlotContext), rising from within it; on desktop
 * it floats at the bottom of the window.
 */
export const SelectionBar = ({
  open,
  count,
  total,
  menuItems,
  primary,
  onCancel,
}: Props) => {
  const slot = useBottomBarSlot();
  const isMobile = useIsMobile();
  const content = (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={onCancel}
        aria-label="Clear selection"
        className="shrink-0 rounded-full"
      >
        <X className="size-5" />
      </Button>
      <div className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate font-semibold">{count} selected</span>
        {/* One line: on a phone the bar is the tab bar's height and can't
            grow, so a wrapped line would be cut off. */}
        <span
          className="text-muted-foreground truncate text-sm tabular-nums"
          title={total}
        >
          {total}
        </span>
      </div>
      <PopoverMenu
        above
        renderTrigger={({ref, onClick}) => (
          <Button
            ref={ref}
            variant="outline"
            onClick={onClick}
            disabled={count === 0}
            className="shrink-0"
          >
            More
          </Button>
        )}
      >
        {menuItems}
      </PopoverMenu>
      {primary && (
        <Button
          variant="primary"
          className="shrink-0"
          disabled={primary.disabled || count === 0}
          onClick={primary.onClick}
        >
          {primary.label}
        </Button>
      )}
    </>
  );
  const bar = (className: string) =>
    open && (
      <motion.div
        key="selection-bar"
        initial={{y: '110%'}}
        animate={{y: 0}}
        exit={{y: '110%'}}
        transition={{duration: 0.22, ease: 'easeOut'}}
        role="toolbar"
        aria-label="Selected rows"
        className={cn('bg-background flex items-center gap-2', className)}
      >
        {content}
      </motion.div>
    );
  if (slot != null) {
    return createPortal(
      <AnimatePresence>
        {bar('absolute inset-0 px-3 pb-[env(safe-area-inset-bottom)]')}
      </AnimatePresence>,
      slot,
    );
  }
  // A phone with no slot is one typing, with the tab bar tucked away.
  if (isMobile) return null;
  return (
    <Layer>
      {bar(
        'border-border fixed bottom-6 left-1/2 z-[1] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 rounded-full border px-3 py-2 shadow-lg',
      )}
    </Layer>
  );
};
